import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, FileSignature, Loader2, ShieldAlert } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import OrgPicker from '../../components/cultivar/OrgPicker';
import { Fingerprint } from '../../components/cultivar/contracts/ContractBits';
import cultivarService, { type IssueLicenseInput } from '../../services/cultivar';
import contractService from '../../services/contracts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { ApiError } from '../../types/api';
import type { Cultivar, CultivarProgram, RegionalLicense } from '../../types/cultivar';
import type { ContractFamily, LicensePreview } from '../../types/contracts';

interface Contact {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

const STATUS_LABELS: Record<RegionalLicense['status'], string> = {
  pending_confirmation: 'Waiting for signature',
  active: 'Signed — sees the program',
  expired: 'Expired',
  suspended: 'Suspended',
  terminated: 'Terminated',
  rejected: 'Declined',
  superseded: 'Replaced',
};

const STATUS_CLASSES: Record<RegionalLicense['status'], string> = {
  pending_confirmation: 'bg-amber-50 text-amber-800 border-amber-200',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  expired: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  suspended: 'bg-orange-50 text-orange-800 border-orange-200',
  terminated: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  superseded: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

// A program master agreement licenses an organisation into a whole program
// (GRIP, say) rather than one cultivar. It is sent and signed like any other
// agreement; once signed and active the licensee sees every cultivar in the
// program, including ones added later. It grants sight only — planting or
// propagating still needs that cultivar's own licence.
export default function ProgramAgreements() {
  const { id } = useParams<{ id: string }>();
  const programId = id ? parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [program, setProgram] = useState<CultivarProgram | null>(null);
  const [cultivars, setCultivars] = useState<Cultivar[]>([]);
  const [agreements, setAgreements] = useState<RegionalLicense[]>([]);
  const [families, setFamilies] = useState<ContractFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [familyId, setFamilyId] = useState<number | 0>(0);
  const [licensee, setLicensee] = useState<{ id: number; name: string } | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [notifyUserId, setNotifyUserId] = useState<number | 0>(0);
  const [region, setRegion] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [mergeValues, setMergeValues] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<string[]>([]);
  const [preview, setPreview] = useState<LicensePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(programId)) return;
    setLoading(true);
    setError(null);
    try {
      const [pRes, cRes, aRes, fRes] = await Promise.all([
        cultivarService.getProgram(programId),
        cultivarService.searchCultivars({ program_id: programId, limit: 100 }),
        cultivarService.getMyLicenses({ role: 'licensor', license_tier: 'program_master', program_id: programId, limit: 100 }),
        contractService.list({ license_tier: 'program_master', published_only: true }),
      ]);
      if (pRes.success && pRes.data) setProgram(pRes.data);
      if (cRes.success && cRes.data) setCultivars(cRes.data.cultivars);
      if (aRes.success && aRes.data) setAgreements(aRes.data.licenses);
      if (fRes.success) setFamilies(fRes.data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the program');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  // One published program contract is plainly the one to send.
  useEffect(() => {
    if (families.length === 1) setFamilyId(families[0].id);
  }, [families]);

  useEffect(() => {
    setNotifyUserId(0);
    setContacts([]);
    if (!licensee) return;
    let cancelled = false;
    (async () => {
      setContactsLoading(true);
      try {
        const res = await cultivarService.getOrgCultivarContacts(licensee.id);
        if (!cancelled && res.success && res.data) setContacts(res.data);
      } catch { /* ignore */ } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [licensee]);

  const payload = useMemo((): IssueLicenseInput => ({
    family_id: familyId || null,
    cultivar_id: null,
    program_id: programId,
    licensee_org_id: licensee?.id || 0,
    license_tier: 'program_master',
    region: region || null,
    start_date: startDate || null,
    end_date: endDate || null,
    tolerance_pct: 0,
    merge_values: mergeValues,
  }), [familyId, programId, licensee, region, startDate, endDate, mergeValues]);

  // Anything that changes the text invalidates what was previewed.
  useEffect(() => { setPreview(null); }, [payload]);

  const missing = (): string | null => {
    if (!familyId) return 'Pick the contract to send';
    if (!licensee) return 'Pick the organisation to send it to';
    return null;
  };

  const runPreview = async () => {
    setError(null);
    const m = missing();
    if (m) { setError(m); return; }
    setPreviewing(true);
    try {
      const res = await cultivarService.previewLicense(payload);
      if (res.success) {
        setPreview(res.data);
        setCustomFields(res.data.custom_fields);
      } else {
        setError(res.error.message);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the agreement');
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    setError(null);
    if (!preview) { setError('Review the agreement first'); return; }
    if (!notifyUserId) { setError('Pick who receives the agreement — terms only go to a named contact'); return; }
    setSending(true);
    try {
      const res = await cultivarService.issueLicenseFromTemplate({
        ...payload,
        notify_user_id: notifyUserId,
        expected_content_hash: preview.content_hash,
      });
      if (res.success) {
        setSent(res.data.id);
        setLicensee(null);
        setPreview(null);
        setMergeValues({});
        setCustomFields([]);
        await load();
      } else {
        setError(res.error.message);
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CONTRACT_CHANGED') setPreview(null);
      setError(e instanceof Error ? e.message : 'Sending failed');
    } finally {
      setSending(false);
    }
  };

  if (licenseLoading || loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Cultivar PBR or admin seat required</h3>
            <p className="text-sm text-gray-600">Sending program agreements needs a cultivar PBR or admin licence seat.</p>
          </div>
        </CardContent></Card>
      </div>
    );
  }
  if (!program) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6">
          <p className="text-gray-700">{error || 'Program not found.'}</p>
          <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')} className="mt-3">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to programs
          </Button>
        </CardContent></Card>
      </div>
    );
  }

  const live = agreements.filter(a => a.status === 'active' || a.status === 'pending_confirmation' || a.status === 'suspended');
  const past = agreements.filter(a => !live.includes(a));

  return (
    <>
      <PageHeader
        title={`Agreements — ${program.name}`}
        subtitle="Organisations that have signed this program's master agreement see every cultivar in it."
        icon={<FileSignature className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div>
          <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Programs
          </Button>
        </div>

        {sent && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Agreement #{sent} sent. The organisation sees the program once it signs.{' '}
              <Link to={`/cultivar/licences/${sent}/agreement`} className="underline">Open it</Link>
            </span>
          </div>
        )}

        <Card>
          <CardHeader><CardTitle>Who holds it</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {live.length === 0 ? (
              <p className="text-sm text-neutral-500">Nobody yet. Send the agreement below.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {live.map(a => (
                  <li key={a.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Link to={`/cultivar/licences/${a.id}/agreement`} className="font-medium text-primary-700 hover:underline break-words min-w-0">
                      {a.licensee_org_name || `Organisation #${a.licensee_org_id}`}
                    </Link>
                    <span className={`text-xs border rounded-full px-2 py-0.5 ${STATUS_CLASSES[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                    {a.end_date && (
                      <span className="text-xs text-neutral-500">until {formatLocalDateInTz(String(a.end_date).slice(0, 10), orgTz)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {past.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-neutral-500">Earlier agreements ({past.length})</summary>
                <ul className="mt-1 space-y-1">
                  {past.map(a => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2">
                      <Link to={`/cultivar/licences/${a.id}/agreement`} className="text-neutral-700 hover:underline break-words min-w-0">
                        {a.licensee_org_name || `Organisation #${a.licensee_org_id}`}
                      </Link>
                      <span className={`text-xs border rounded-full px-2 py-0.5 ${STATUS_CLASSES[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Send the agreement</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-neutral-600">
              Once signed, the organisation sees all {cultivars.length} cultivar{cultivars.length === 1 ? '' : 's'} in{' '}
              {program.name}, and any you add later. It gets no right to plant or propagate them: each of those
              still needs that cultivar's own licence.
            </p>
            {cultivars.length > 0 && (
              <p className="text-xs text-neutral-500 break-words">
                {cultivars.map(c => c.trade_name || c.name).join(', ')}
              </p>
            )}

            <div>
              <label htmlFor="program-contract" className="block text-sm font-semibold text-neutral-700 mb-1">Contract *</label>
              {families.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  No published program master contract.{' '}
                  <Link to="/cultivar/contracts" className="underline">Write and publish one</Link> first.
                  There is a starter text for it.
                </div>
              ) : (
                <select
                  id="program-contract"
                  value={familyId}
                  onChange={e => setFamilyId(Number(e.target.value))}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value={0}>— pick a contract —</option>
                  {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              )}
            </div>

            <div>
              <p className="block text-sm font-semibold text-neutral-700 mb-1">Organisation *</p>
              <OrgPicker value={licensee} onChange={setLicensee} placeholder="Search organisations by name…" />
            </div>

            {licensee && (
              <div className="space-y-1">
                <label htmlFor="program-recipient" className="block text-sm font-semibold text-neutral-700">Send to *</label>
                <p className="text-xs text-neutral-500">
                  The person at {licensee.name} who receives the agreement and signs it. Terms only go to this one contact.
                </p>
                {contactsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading contacts…
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    This organisation has no cultivar user to send it to. It needs at least one active cultivar user first.
                  </div>
                ) : (
                  <select
                    id="program-recipient"
                    value={notifyUserId}
                    onChange={e => setNotifyUserId(Number(e.target.value))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={0}>— pick a recipient —</option>
                    {contacts.map(c => (
                      <option key={c.id} value={c.id}>
                        {`${c.first_name} ${c.last_name}`.trim() || c.email} — {c.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Region" aria-label="Region" value={region} onChange={e => setRegion(e.target.value)} placeholder="e.g. Australia" />
              <Input label="Start date" aria-label="Start date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              <Input label="End date" aria-label="End date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>

            {customFields.length > 0 && (
              <div>
                <p className="block text-sm font-semibold text-neutral-700 mb-2">Fields this contract asks you to fill in</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {customFields.map(f => (
                    <Input
                      key={f}
                      label={f.replace(/_/g, ' ')}
                      value={mergeValues[f] || ''}
                      onChange={e => setMergeValues({ ...mergeValues, [f]: e.target.value })}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {preview && (
          <Card>
            <CardHeader><CardTitle>The agreement that will be sent</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-neutral-700">
                <strong>{preview.family.name}</strong> v{preview.version.version}
              </p>
              <Fingerprint hash={preview.content_hash} label="Fingerprint of this text" />
              {preview.blank_fields.length > 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Left to fill in by hand: {preview.blank_fields.map(f => f.replace(/_/g, ' ')).join(', ')}.
                </p>
              )}
              <pre className="whitespace-pre-wrap break-words font-sans text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded p-3 max-h-[32rem] overflow-y-auto">
                {preview.merged_terms}
              </pre>
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm flex gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {!preview ? (
            <Button variant="primary" onClick={runPreview} disabled={previewing}>
              {previewing ? 'Building…' : 'Review agreement'}
            </Button>
          ) : (
            <Button variant="primary" onClick={send} disabled={sending || !notifyUserId}>
              {sending ? 'Sending…' : 'Send for signature'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
